//Copyright (c) 2024 Greener Power Solutions.
//This file is part of Greener Power Solutions' software,
//and may not be altered, copied, redistributed, or sold
//without specific permission.
import { CompareFn, FilterValue, Filters } from './useFilter.types';
import { get } from 'lodash';

function extractFilterParts(key) {
  const parts = key.split('__');

  // If the key does not follow the pattern, treat it as a standalone property
  if (parts.length === 1) {
    return { property: parts[0], compareFnKey: undefined };
  }

  const compareFnKey = parts.pop();
  const property = parts.join('__');
  return { property, compareFnKey };
}

export const makeFilterKey = (property: string, compareFnKey: string) => {
  return `${property}__${compareFnKey}`;
};

export const generateQueryString = (filters: Filters): string => {
  const searchParams = new URLSearchParams();

  for (const { value, filterKey } of filters) {
    if (value || (Array.isArray(value) && !!value.length)) {
      searchParams.set(filterKey, value.toString());
    }
  }
  return searchParams.toString();
};

export const queryStringToUrlState = (queryString: string): Record<string, string> => {
  const params = new URLSearchParams(queryString);
  const state: Record<string, string> = {};
  params.forEach((value, key) => {
    state[key] = value;
  });
  return state;
};

export const compareFunctions: Record<string, CompareFn> = {
  eq: (itemValue, filterValue) => {
    if (Array.isArray(itemValue) || Array.isArray(filterValue)) {
      if (Array.isArray(itemValue) && Array.isArray(filterValue)) {
        return itemValue.some((val) => filterValue.includes(val));
      }
      return false;
    }
    if (typeof itemValue === 'string' && typeof filterValue === 'string') {
      return itemValue.toLowerCase() === filterValue.toLowerCase();
    }
    return itemValue === filterValue;
  },
  ge: (itemValue, filterValue) => {
    if (!Array.isArray(itemValue) && !Array.isArray(filterValue)) {
      return itemValue >= filterValue;
    }
    return false;
  },
  le: (itemValue, filterValue) => {
    if (!Array.isArray(itemValue) && !Array.isArray(filterValue)) {
      return itemValue <= filterValue;
    }
    return false;
  },
  contains: (itemValue, filterValue) => {
    if (typeof itemValue === 'string' && typeof filterValue === 'string') {
      return itemValue.toLowerCase().includes(filterValue.toLowerCase());
    }
    if (Array.isArray(filterValue)) {
      return filterValue.includes(itemValue as string);
    }
    return false;
  },
};

/**
 * Processes a filter on a given item.
 *
 * @template T
 * @param {T} item - The item to filter.
 * @param {FilterValue} filterValue - The filter value.
 * @param {string} compareFnKey - The comparison function key.
 * @param {string | string[]} property - The property or properties to filter.
 * @returns {boolean} Whether the item matches the filter.
 */
export const processFilter = <T>(
  item: T,
  filterValue: FilterValue,
  compareFnKey: string | undefined,
  property: string | string[]
): boolean => {
  if (!filterValue || (Array.isArray(filterValue) && !filterValue.length)) {
    return true;
  }

  const compareFn: CompareFn = compareFunctions[compareFnKey ?? 'eq'];

  if (Array.isArray(property)) {
    return property.some((prop) => filterNested(item, prop, filterValue, compareFn));
  } else if (typeof property === 'string') {
    return filterNested(item, property, filterValue, compareFn);
  }

  return true; // Skip filtering if no valid property
};

export const initializeFiltersFromUrlState = (
  urlState: Record<string, string>,
  isValidFilterProperty: (properties: string) => boolean
): Filters => {
  const newFilters: Filters = [];

  Object.entries(urlState).forEach(([key, value]) => {
    const { property, compareFnKey } = extractFilterParts(key);

    const parsedValue = value.includes(',') ? value.split(',') : value;

    if (isValidFilterProperty(property)) {
      newFilters.push({
        filterKey: key,
        value: parsedValue,
        property,
        compareFnKey,
      });
    }
  });
  return newFilters;
};

export const filterSingleItem = (itemValue: FilterValue, filterValue: FilterValue, compareFn: CompareFn): boolean => {
  return compareFn(itemValue, filterValue);
};

export const filterNested = <T>(item: T, path: string, filterValue: FilterValue, compareFn: CompareFn): boolean => {
  const pathSegments = path.split('.');
  let currentValue = item;

  for (const segment of pathSegments) {
    currentValue = get(currentValue, segment);
    if (Array.isArray(currentValue)) {
      const remainingPath = pathSegments.slice(pathSegments.indexOf(segment) + 1).join('.');
      return currentValue.some((arrItem) =>
        filterSingleItem(get(arrItem, remainingPath) as FilterValue, filterValue, compareFn)
      );
    }

    if (currentValue === undefined) {
      return false;
    }
  }

  return filterSingleItem(currentValue as FilterValue, filterValue, compareFn);
};
